'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/Button';
import { validateDemoForm, hasErrors, type FormErrors } from '@/lib/form-validation';
import { getAllTrackingData } from '@/lib/tracking';
import { getConsentSnapshot, getSavedConsent } from '@/lib/consent';
import Step1Industry from './FormSteps/Step1Industry';
import Step2RevenueNew from './FormSteps/Step2RevenueNew';
import Step3ContactNew from './FormSteps/Step3ContactNew';
import ReCaptchaLoader from './ReCaptchaLoader';
import styles from './DemoForm.module.css';

declare global {
  interface Window {
    grecaptcha?: {
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

export interface DemoFormProps {
  locale: string;
  translations: any;
}

export interface FormData {
  business_type: string;
  annual_revenue: string;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  company: string;
  message: string;
}

export default function DemoForm({ locale, translations }: DemoFormProps) {
  const router = useRouter();
  
  // Extract the form translations
  const t = translations.demoRequest?.form || translations.form || {};
  
  // START AT STEP 3 - Skip industry and revenue selection (temporarily disabled)
  const [currentStep, setCurrentStep] = useState(3);
  const [formData, setFormData] = useState<FormData>({
    business_type: 'not_specified', // Default value since step 1 is skipped
    annual_revenue: 'not_specified', // Default value since step 2 is skipped
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formStartTime] = useState(Date.now());
  const [honeypot, setHoneypot] = useState('');

  // Track form abandonment
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Only track if form was started but not submitted (step 3 only now)
      if (currentStep === 3 && !isSubmitting) {
        // Track form abandonment event
        if (typeof window !== 'undefined' && window.dataLayer) {
          window.dataLayer.push({
            event: 'form_abandonment',
            form_step: currentStep,
            form_type: 'demo_request',
          });
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentStep, isSubmitting]);

  // Track step views
  useEffect(() => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'form_step_view',
        form_step: currentStep,
        form_type: 'demo_request',
      });
    }
  }, [currentStep]);

  const handleNext = () => {
    // Validate current step
    const stepErrors: FormErrors = {};
    
    if (currentStep === 1) {
      if (!formData.business_type) {
        stepErrors.business_type = t.errors.businessTypeRequired;
      }
    } else if (currentStep === 2) {
      if (!formData.annual_revenue) {
        stepErrors.annual_revenue = t.errors.revenueRequired;
      }
    }
    
    if (hasErrors(stepErrors)) {
      setErrors(stepErrors);
      return;
    }
    
    setErrors({});
    setCurrentStep(currentStep + 1);
  };

  const handleAutoAdvance = () => {
    // For auto-advance, we skip validation since a selection was just made
    // The state might not be updated yet due to React's async nature
    setErrors({});
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setErrors({});
  };

  const handleSubmit = async () => {
    // Anti-spam: Timing check (must take at least 3 seconds)
    const timeSpent = Date.now() - formStartTime;
    if (timeSpent < 3000) {
      setSubmitError(t.errors.tooFast);
      return;
    }

    // Validate all fields
    const validationErrors = validateDemoForm(formData);
    
    if (hasErrors(validationErrors)) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Get reCAPTCHA v3 token (skip on localhost for testing)
      const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
      const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
      
      let recaptchaToken = 'localhost-test-token';
      
      if (!isLocalhost) {
        if (!recaptchaSiteKey || !window.grecaptcha) {
          throw new Error('reCAPTCHA not initialized');
        }

        recaptchaToken = await window.grecaptcha.execute(recaptchaSiteKey, {
          action: 'demo_request',
        });
      }

      // Get consent status
      const savedConsent = getSavedConsent();
      const hasAnalyticsConsent = savedConsent?.analytics || false;

      // Only get tracking data if analytics consent is granted (GDPR compliance)
      const trackingData = hasAnalyticsConsent ? getAllTrackingData() : {};

      // Get consent snapshot for GDPR audit trail (always included)
      const consentSnapshot = getConsentSnapshot();

      // Prepare complete form data with locale
      const completeFormData = {
        ...formData,
        ...trackingData,  // Only includes UTM, device, etc. if analytics consent granted
        consent_snapshot: consentSnapshot,  // Always included for audit trail
        page_title: document.title,  // Always included (not PII)
        page_locale: locale,  // Add locale for proper redirect
        // Note: origin_url and referrer_url are included in trackingData (only with consent)
      };

      const response = await fetch('/api/submit-demo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formData: completeFormData,
          recaptchaToken,
          honeypot,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Track conversion with Enhanced Conversions data
        if (typeof window !== 'undefined') {
          // Import trackConversion dynamically
          import('@/lib/analytics').then(({ trackConversion }) => {
            trackConversion('demo_request', {
              email: formData.email,
              phone: formData.phone,
              firstName: formData.firstname,
              lastName: formData.lastname,
            });
          });
          
          // Also push detailed event to dataLayer
          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'lead_submit',
              form_type: 'demo_request',
              business_type: formData.business_type,
              annual_revenue: formData.annual_revenue,
            });
          }
        }

        // Redirect to thank you page
        router.push(result.redirectUrl || `/${locale === 'en' ? '' : locale + '/'}thank-you`);
      } else {
        const errorMsg = t.errors?.submitFailed || 'Er ging iets mis. Probeer het later opnieuw.';
        const emailFallback = t.errors?.emailFallback || 'Je kunt ook direct mailen naar info@jamezz.nl';
        setSubmitError(`${errorMsg} ${emailFallback}`);
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      const errorMsg = t.errors?.submitFailed || 'Er ging iets mis. Probeer het later opnieuw.';
      const emailFallback = t.errors?.emailFallback || 'Je kunt ook direct mailen naar info@jamezz.nl';
      setSubmitError(`${errorMsg} ${emailFallback}`);
      setIsSubmitting(false);
    }
  };

  const updateField = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    // Clear error for this field
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  // ADJUSTED: Since we only show step 3, progress is always 100%
  const totalSteps = 3;
  const progress = 100; // Always show full progress since we skip to final step

  return (
    <div className={styles.formContainer}>
      {/* Load reCAPTCHA only on form pages */}
      <ReCaptchaLoader />
      
      {/* Progress bar - now hidden since we only show 1 step */}
      <div className={styles.progressBar} style={{ display: 'none' }}>
        <div className={styles.progressFill} style={{ width: `${progress}%` }} />
      </div>

      {/* Step indicator - now hidden since we only show 1 step */}
      <div className={styles.stepIndicator} style={{ display: 'none' }}>
        <span className={styles.stepText}>
          {t.step} {currentStep} {t.of} {totalSteps}
        </span>
      </div>

      {/* Honeypot field (hidden from users, trap for bots) */}
      <input
        type="text"
        name="website_url"
        id="website_field"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        autoComplete="off"
        tabIndex={-1}
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
        aria-hidden="true"
      />

      {/* Form steps - Only show step 3 (contact form) */}
      <div className={styles.formContent}>
        {/* Steps 1 and 2 are temporarily disabled */}
        {/* They can be re-enabled by changing currentStep initial value to 1 */}
        
        {currentStep === 3 && (
          <Step3ContactNew
            formData={formData}
            onChange={updateField}
            errors={errors}
            translations={translations}
            locale={locale}
          />
        )}

        {/* Error message */}
        {submitError && (
          <div className={styles.errorMessage} role="alert">
            {submitError}
          </div>
        )}

        {/* Navigation buttons - Only show submit button since we're on final step */}
        <div className={styles.formActions}>
          {/* Back button hidden since there are no previous steps */}
          
          {currentStep === totalSteps && (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? t.submitting : t.submit}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

